import React from 'react';

interface TeamMemberProps {
  name: string;
  role: string;
  imageUrl: string;
  description: string;
}

const TeamMemberCard: React.FC<TeamMemberProps> = ({ name, role, imageUrl, description }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 text-center flex flex-col items-center transform hover:scale-105 transition-transform duration-300 h-full">
      <img 
        src={imageUrl} 
        alt={name}
        className="w-32 h-32 mx-auto rounded-full object-cover shadow-md border-4 border-jskool-gold/50"
      />
      <h3 className="mt-4 font-heading text-xl font-bold text-jskool-dark">{name}</h3>
      <p className="text-jskool-blue font-semibold mt-1">{role}</p>
      <p className="text-sm text-jskool-dark/70 mt-3 flex-grow">{description}</p>
    </div>
  );
};

const TeamSection: React.FC = () => {
  const placeholderImageUrl = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png";
  
  const teamMembers = [
    {
      name: "Mohit Jain",
      role: "Knowledge Enabler",
      imageUrl: placeholderImageUrl,
      description: "Mohit hails from a Jain family in Delhi and grew up in Bangalore. With 13 years in IT, he’s on a journey to deepen his understanding of Jain principles while sharing modern, tech-driven experiences that make these teachings relatable for today’s youth. As JSkool’s guiding force, he blends technical expertise with insights he’s gathered along the way, empowering young minds to explore mindfulness and non-attachment to navigate challenges and live with purpose in a fast-paced world."
    },
    {
      name: "Ayushi Jain",
      role: "Community Connection Champion",
      imageUrl: placeholderImageUrl,
      description: "Ayushi is an HR professional passionate about helping others uncover the value of Jain heritage through her own evolving journey. She creates welcoming spaces for students to explore principles like compassion and right conduct through interactive discussions and events. Ayushi believes authentic growth comes from meaningful relationships and uses her people skills to guide youth as they embrace Jain values for conscious living and stronger bonds."
    },
    {
      name: "Tarun Jain",
      role: "Tech Innovation Explorer",
      imageUrl: placeholderImageUrl,
      description: "Born in Delhi, Tarun is an IT Engineer whose 11-year career reflects his ongoing exploration of Jain ideals—simplicity, kindness, and non-violence. As JSkool’s digital architect, he designs apps and platforms infused with lessons on self-discipline and equanimity, drawing from both his technical background and the insights he gains daily. Tarun’s goal is to create digital spaces where youth can engage deeply with timeless teachings and cultivate inner peace."
    },
    {
      name: "Kajal Jain",
      role: "Youth Reconnection Facilitator",
      imageUrl: placeholderImageUrl,
      description: "Originally from Bengaluru and now pursuing an MCA in Data Science in Delhi NCR, Kajal blends her analytical skills with her rediscovered passion for Jain wisdom. Through her own experience of reconnecting with Jain principles, she leads reflective workshops that mix data-driven insights with teachings on forgiveness and patience. Kajal’s journey inspires youth to see their cultural heritage as a living toolkit for navigating life’s complexities with grace and compassion."
    },
    {
      name: "Ishu Jain",
      role: "Happiness & Culture Ambassador",
      imageUrl: placeholderImageUrl,
      description: "Ishu, a Delhi-born market research analyst with nine years of experience, is on a personal quest to deepen her grasp of Jain teachings. Her extroverted nature and love for travel make her a natural storyteller who weaves lessons on gratitude and right intention into everyday conversations. Through meetups and social media, she invites youth to join her in exploring how Jain values can amplify joy, foster resilience, and align personal ambitions with inner fulfillment."
    }
  ];
  
  return (
    <section id="team" className="py-20 bg-jskool-cream">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-heading font-extrabold text-jskool-dark">Our Core Team</h2>
          <p className="mt-4 text-lg text-jskool-dark/70 max-w-3xl mx-auto">
            Meet the dedicated individuals passionate about reimagining Jain education for the modern world.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {teamMembers.map(member => (
            <TeamMemberCard key={member.name} {...member} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;